import React from "react";
import { motion } from "framer-motion";
import {
  FaLaptopCode,
  FaProjectDiagram,
  FaUsers,
  FaClock,
  FaBook,
  FaUserTie,
  FaChalkboardTeacher,
} from "react-icons/fa";

// Local images
import infosys from "./infosys.png";
import google from "./google.png";
import wipro from "./Wipro.png";
import amazon from "./amazon.png";
import capgemini from "./Capgemini.png";

const MadHighlights = () => {
  return (
    <main className="bg-gradient-to-br from-red-700 via-blue-800 to-blue-900 text-white">
      {/* Why Choose MAD Academy */}
      <section className="py-16 bg-white/10 backdrop-blur-sm">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6 text-white">
            Why Choose MAD Academy?
          </h2>
          <p className="text-white/80 max-w-2xl mx-auto mb-10">
            We combine cutting-edge technology training with real-world projects
            and personalized mentorship. Our goal is to turn passionate learners
            into confident professionals.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: <FaLaptopCode size={28} />,
                title: "Job-ready Skills",
                color: "from-blue-500 to-red-500",
              },
              {
                icon: <FaProjectDiagram size={28} />,
                title: "Real Projects",
                color: "from-red-600 to-blue-600",
              },
              {
                icon: <FaUsers size={28} />,
                title: "Global Community",
                color: "from-blue-600 to-red-500",
              },
              {
                icon: <FaClock size={28} />,
                title: "Flexible Learning",
                color: "from-red-500 to-blue-700",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.2 }}
                className={`p-6 bg-gradient-to-r ${item.color} text-white rounded-xl shadow-md hover:shadow-lg transition flex flex-col items-center gap-3`}
              >
                {item.icon}
                <h4 className="font-semibold text-lg">{item.title}</h4>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Impact at a Glance */}
      <section className="py-16 bg-white/10 backdrop-blur-sm">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-2 text-white">
            Our Impact at a Glance
          </h2>
          <p className="text-white/80 mb-10">
            Transforming careers and building futures with proven results.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: <FaUsers size={36} className="text-yellow-300" />,
                number: "600+",
                label: "Students Enrolled",
              },
              {
                icon: <FaBook size={36} className="text-pink-300" />,
                number: "5+",
                label: "Trainings Available",
              },
              {
                icon: <FaUserTie size={36} className="text-yellow-300" />,
                number: "100%+",
                label: "Successful Placements",
              },
              {
                icon: (
                  <FaChalkboardTeacher size={36} className="text-pink-300" />
                ),
                number: "100+",
                label: "Expert Mentors",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.2 }}
                className="p-6 bg-white/10 rounded-xl shadow hover:shadow-lg transition flex flex-col items-center gap-3"
              >
                {item.icon}
                <h3 className="text-2xl font-bold text-white">{item.number}</h3>
                <p className="text-white/80">{item.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Alumni Work At */}
      <section className="py-16 bg-white/10 backdrop-blur-sm">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-2 text-white">
            Our Alumni Work At
          </h2>
          <p className="text-white/80 mb-10">
            We partner with leading tech companies to connect our students with
            top career opportunities.
          </p>

          <div className="flex flex-wrap justify-center items-center gap-10 md:gap-16">
            {[infosys, google, wipro, amazon, capgemini].map((logo, i) => (
              <motion.img
                key={i}
                src={logo}
                alt={`Company ${i + 1}`}
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.2 }}
                className="w-28 h-auto object-contain grayscale hover:grayscale-0 transition"
              />
            ))}
          </div>
        </div>
      </section>

      {/* Your Path to a Tech Career */}
      <section className="py-20 bg-white/10 backdrop-blur-sm">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-3 text-white">
            Your Path to a Tech Career
          </h2>
          <p className="text-white/80 mb-16 max-w-2xl mx-auto">
            A simple, proven 4-step journey from learning the fundamentals to
            launching your new career.
          </p>

          <div className="relative max-w-4xl mx-auto">
            {/* Vertical Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full border-l-4 border-white/30"></div>

            {/* Steps */}
            {[
              {
                title: "Enroll & Master the Skills",
                desc: "Begin your journey with our expert-led curriculum, focusing on in-demand technologies and hands-on learning from day one.",
              },
              {
                title: "Build Your Project Portfolio",
                desc: "Apply your skills by building a portfolio of real-world projects that showcase your capabilities to employers.",
              },
              {
                title: "Get Interview-Ready",
                desc: "Work with our placement cell on resume building, mock interviews, and soft skills to boost confidence.",
              },
              {
                title: "Launch Your Career",
                desc: "Get connected with top hiring partners and start applying for roles in the tech industry.",
              },
            ].map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.2 }}
                className={`relative flex flex-col md:flex-row items-center mb-16 ${
                  i % 2 === 0 ? "md:justify-start" : "md:justify-end"
                }`}
              >
                <div className="absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-white rounded-full border-4 border-white/50 shadow-lg z-10"></div>

                <div
                  className={`mt-8 md:mt-0 md:w-5/12 bg-white text-gray-900 rounded-xl p-6 shadow-lg ${
                    i % 2 === 0
                      ? "md:mr-auto md:text-right"
                      : "md:ml-auto md:text-left"
                  }`}
                >
                  <h3 className="text-xl font-bold text-blue-700">
                    {step.title}
                  </h3>
                  <p className="mt-2 text-gray-700">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default MadHighlights;
